import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Eye, Calendar, Search, PlusCircle, Pencil, Loader2 } from 'lucide-react';
import { useVendorInvoices } from '../../hooks/useVendorInvoices';
import { useVendorProfile } from '../../services/vendorProfileService';
import { vendorInvoiceApi } from '../../services/vendorInvoiceService';
import { downloadInvoicePdfFromInvoiceData } from '../../utils/vendorInvoicePdf';
import { useToastContext } from '../../contexts/ToastContext';

const VendorInvoices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const { error: showErrorToast } = useToastContext();
  const { data: profileResp } = useVendorProfile();

  const {
    invoices,
    isLoading,
    error,
    pagination,
    formatAmount,
    formatDate,
    getStatusColor
  } = useVendorInvoices(currentPage, 15, {
    search: searchTerm
  });

  const handleDownload = useCallback(
    async (invoiceId) => {
      setDownloadingId(invoiceId);
      try {
        const res = await vendorInvoiceApi.getOne(invoiceId);
        const inv = res?.data;
        const vp = profileResp?.data ?? {};
        if (inv) await downloadInvoicePdfFromInvoiceData(inv, vp);
      } catch {
        showErrorToast('Download failed', 'Could not download invoice. Try again.');
      } finally {
        setDownloadingId(null);
      }
    },
    [profileResp, showErrorToast]
  );

  const canEditInvoice = (inv) =>
    inv?.status && !['paid', 'cancelled'].includes(String(inv.status).toLowerCase());

  return (
    <>
     <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
     <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Invoices</h1>
            <p className="text-sm text-gray-600">Create, send, and download invoices for clients, guests, or custom work</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4 sm:mt-0 w-full sm:w-auto">
            <Link
              to="/vendor/invoices/create"
              className="sm:order-last inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-sm bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 w-full sm:w-auto"
            >
              <PlusCircle className="w-4 h-4" />
              Custom invoice
            </Link>
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            Failed to load invoices. Please try again.
          </div>
        ) : invoices.length > 0 ? (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-semibold text-gray-900">
                          Invoice #{invoice.invoice_number || invoice.id}
                        </p>
                        {invoice.client_name && (
                          <p className="text-sm text-gray-500">{invoice.client_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {formatDate(invoice.invoice_date || invoice.created_at)}
                        </span>
                      </div>
                      {invoice.total_amount && (
                        <span className="font-semibold text-gray-900">
                          ${formatAmount(invoice.total_amount)}
                        </span>
                      )}
                      {invoice.status && (
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(invoice.status)}`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/vendor/invoices/${invoice.id}`}
                      className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-900"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">View</span>
                    </Link>
                    {canEditInvoice(invoice) && (
                      <Link
                        to={`/vendor/invoices/${invoice.id}/edit`}
                        className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-900"
                      >
                        <Pencil className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDownload(invoice.id)}
                      disabled={downloadingId === invoice.id}
                      className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm disabled:opacity-60"
                    >
                      {downloadingId === invoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">Download</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 sm:p-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm ? 'No invoices match your search' : 'No invoices found'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-700">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={pagination.current_page === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let page;
                  if (pagination.last_page <= 5) {
                    page = i + 1;
                  } else if (pagination.current_page <= 3) {
                    page = i + 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    page = pagination.last_page - 4 + i;
                  } else {
                    page = pagination.current_page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                        pagination.current_page === page
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        </div>
        </div>
    </>
  );
};

export default VendorInvoices;

